import { trackCustomEvent } from "@amzn/uxm-stencil-react-components/lib/src/uxm/custom-event";
import { CatalogicalItem, CatalogicalRequestContent } from "common/types/catalogical-type";
import { config } from "config";
import { AIForMeTool } from "lib/models/aiforme-models";
import { ItemModel } from "lib/services/catalogical-models";
import { SearchExperienceDomains } from "lib/services/shop-search-service";
import { v4 as uuidv4 } from "uuid";

export const ONE_INDEX_BASED_RANK = 1;
const ITPT_FOUNDATIONAL_KEYWORD = "Foundational";

// Event touchpoint names
export const TOUCHPOINTS = {
  // Existing touchpoint, keeping for consistency in metrics
  SEARCH_RESULTS_SHOWN: "search-experience-box-results",
  SEARCH_EXPERIENCE_RESULT_CLICKED: "search-experience-result",

  // Search page
  SEARCH_RESULTS_ERROR: "search-experience-results-error",

  // Search box
  SEARCH_BOX_RESULTS_SHOWN: "search-box-results-shown",
  SEARCH_BOX_RESULT_CLICKED: "search-box-result-clicked",
  SEARCH_BOX_RESULTS_ERROR: "search-box-results-error",

  // Recommended products (PDP)
  RECOMMENDED_PRODUCTS_RESULT_CLICKED: "recommended-products-result-clicked",

  // Non-vendable toggle
  SHOW_NON_VENDABLE_TOGGLED: "search-show-non-vendable-toggled",
} as const;

export enum Bucket {
  Organic = "Organic",
  Foundational = "Foundational",
  Recommended = "Recommended",
}

export enum EventTrigger {
  UserQuery = "userQuery",
  FilterChange = "filterChange",
  SortChange = "sortChange",
  UrlLoad = "urlLoad",
  Unknown = "unknown",
}

// Interfaces
export interface TopNResultItem {
  taxonomyId: string;
  rank: number; // 1-based position across ALL visible results
  bucket?: Bucket;
}

export interface SearchFilters {
  [key: string]: string[] | undefined;
}

export interface SearchSort {
  sortBy?: string;
  sortDirection?: string;
}

export interface SearchTelemetryMetadata {
  queryId: string;
  resultCount: number;
  visibleResultCount: number;
}

// UxM Events payload
export interface BaseSearchContextPayload {
  queryId: string;
}
export interface SearchResultsErrorPayload extends BaseSearchContextPayload {
  error?: string;
}

interface ResultsShownPayload extends BaseSearchContextPayload {
  request: CatalogicalRequestContent;
  searchText: string;
  domain?: SearchExperienceDomains;

  visibleResultsCount: number; // Count of items currently loaded
  resultCount: number; // API total

  topNResults: TopNResultItem[];
}

export interface SearchResultsShownPayload extends ResultsShownPayload {
  domain: SearchExperienceDomains;
  searchText: string;

  filters: SearchFilters;
  sort: SearchSort;

  nextToken: number;
  triggerSearchboxQueryId?: string | null;
  eventTrigger?: EventTrigger;
}

export interface SearchBoxResultsShownPayload extends Partial<ResultsShownPayload> {}

export interface BaseClickPayload {
  queryId: string | null;
  visibleResultsCount?: number; // Amount of results loaded at the time in that context
  totalResultsCount?: number; // Total results from API total

  // Item info
  taxonomyId: string;
  name?: string;
  bucket: Bucket;
  rank?: number; // 1-based, rank across its context
  score?: number;
}

export interface AiForMeClickPayload extends BaseClickPayload {
  title: string;
  sortingPriorityTag?: string;
  isRecommended: boolean;
  recommendedJobFamilies?: string[];
  [key: string]: unknown;
}

function getAppId(): string {
  return config.analytics.uxmAppId;
}

export function generateQueryId(): string {
  return uuidv4();
}

function softwareCalculateBucket(item: CatalogicalItem): Bucket {
  if (item.itptClassification === ITPT_FOUNDATIONAL_KEYWORD) {
    return Bucket.Foundational;
  }
  return Bucket.Organic;
}

function aiformeCalculateBucket(item: AIForMeTool, isRecommended?: boolean): Bucket {
  if (isRecommended) {
    return Bucket.Recommended;
  }

  return Bucket.Organic;
}

export function calculateBucket(item: ItemModel, domain: SearchExperienceDomains, isRecommended?: boolean): Bucket {
  switch (domain) {
    case "software":
      return softwareCalculateBucket(item as CatalogicalItem);
    case "aiforme":
      return aiformeCalculateBucket(item as AIForMeTool, isRecommended);
    default:
      return Bucket.Organic;
  }
}

export function buildTopNResults(
  items: ItemModel[],
  offset: number,
  domain: SearchExperienceDomains,
  nAmount?: number
): TopNResultItem[] {
  const results = items.map(
    (item, index): TopNResultItem => ({
      taxonomyId: item.taxonomyId,
      rank: index + offset + ONE_INDEX_BASED_RANK,
      bucket: calculateBucket(item, domain),
    })
  );

  return nAmount ? results.slice(0, nAmount) : results;
}

// Search page
export function trackSearchResultsShown(payload: SearchResultsShownPayload): void {
  trackEventWithCatch({ ...payload }, TOUCHPOINTS.SEARCH_RESULTS_SHOWN);
}

export function trackSearchResultsError(payload: SearchResultsErrorPayload): void {
  trackEventWithCatch({ ...payload }, TOUCHPOINTS.SEARCH_RESULTS_ERROR);
}

export function trackResultClicked(payload: BaseClickPayload): void {
  trackEventWithCatch({ ...payload }, TOUCHPOINTS.SEARCH_EXPERIENCE_RESULT_CLICKED);
}

// Search Box
export function trackSearchBoxResultsShown(payload: SearchBoxResultsShownPayload): void {
  trackEventWithCatch({ ...payload }, TOUCHPOINTS.SEARCH_BOX_RESULTS_SHOWN);
}

export function trackSearchBoxResultsError(payload: SearchResultsErrorPayload): void {
  trackEventWithCatch({ ...payload }, TOUCHPOINTS.SEARCH_BOX_RESULTS_ERROR);
}

export function trackSearchBoxResultClicked(payload: BaseClickPayload): void {
  trackEventWithCatch({ ...payload }, TOUCHPOINTS.SEARCH_BOX_RESULT_CLICKED);
}

// Recommended Products (PDP)
export function trackRecommendedProductClicked(payload: BaseClickPayload): void {
  trackEventWithCatch({ ...payload }, TOUCHPOINTS.RECOMMENDED_PRODUCTS_RESULT_CLICKED);
}

// Non-vendable toggle
export function trackShowNonVendableToggled(enabled: boolean): void {
  trackEventWithCatch({ enabled }, TOUCHPOINTS.SHOW_NON_VENDABLE_TOGGLED);
}

export function trackEventWithCatch(payload: Record<string, unknown>, touchpointId: string): void {
  try {
    trackCustomEvent(payload, touchpointId, getAppId());
  } catch (error) {
    console.error("Failed to track event:", error);
  }
}
